import { apply, first, group_by, map, object_map, scan } from "@pdfking/rfunc"
import { Identity, JSONDecode, RecordMerge } from "@pdfking/tfunc"
import uuid from "@pdfking/workflow/utils/uuid"
import autocomplete from "../comp/Autocomplete"
import barcode from "../comp/Barcode"
import cart_counter from "../comp/CartCounter"
import collapsable from "../comp/Collapsable"
import double from "../comp/Double"
import double_autocomplete from "../comp/DoubleAutocomplete"
import markdown from "../comp/Markdown"
import megamenu from "../comp/Megamenu"
import messages from "../comp/Messages"
import publicsearch from "../comp/Publicsearch"
import rating from "../comp/Rating"
import routerview from "../comp/Routerview"
import scroll from "../comp/Scroll"
import searchselect from "../comp/Searchselect"
import sizedatalist from "../comp/Sizedatalist"
import uploader from "../comp/Uploader"
import username from "../comp/Username"
import autocomplete_index from "../constants/autocomplete"
import append from "../dom/append"
import element from "../dom/element"
import selector from "../dom/selector"
import register_dom_update from "../navigation/register_dom_update"
import navigate from "../routes/navigate"
import { SLIDESHOW_VIEW } from "../routes/urls"
import user from "../state/user"
import init from "./init"

function require_staff(func, func2) {
  return el => {
    if (user().is_staff) {
      return (func || Identity)(el)
    }
    return (func2 || Identity)(el)
  }
}

const components = {
  autocomplete: el =>
    // @ts-expect-error TS2345
    init(autocomplete, el, {
      multiple: el.matches("[multiple]"),
      options: {},
      value:
        JSONDecode(el.getAttribute("data-initial") || "null") ||
        (el.value ? [{ id: el.value, unicode: el.value }] : []),
    }),
  autocomplete_select: el =>
    autocomplete_index()
      .load_autocomplete(el.getAttribute("data-autocomplete"))
      .then(content => {
        const options = {}

        scan(
          labels =>
            scan(
              (unicode, id) =>
                (options[id] = {
                  group: labels.name,
                  id,
                  unicode,
                }),
              labels.objects
            ),
          content ? content.objects : []
        )

        // @ts-expect-error TS2345
        return init(searchselect, el, {
          options,
          value: el.value,
        })
      }),
  // @ts-expect-error TS2345
  barcode: el => init(barcode, el, { name: el.name, value: el.value }),

  // @ts-expect-error TS2554
  cart_counter: el => init(cart_counter, el),

  // @ts-expect-error TS2345
  collapsable: el => init(collapsable, el, { preserve_class: true }),

  double: el =>
    // @ts-expect-error TS2345
    init(double, el, {
      options: group_by(
        map(
          (el: HTMLOptionElement) =>
            apply(
              (f, s) => ({
                group: s ? f : f,
                label:
                  (el.parentNode as HTMLElement)?.getAttribute("label") || "",
                name: s ? s : "",
                value: el.value,
              }),
              el.innerHTML.split(": ")
            ),
          selector("option", el) as HTMLOptionElement[]
        ),
        info => info.label,
        array =>
          group_by(
            array,
            info => info.group,
            array =>
              object_map(
                info => info.value,
                info => info.name,
                array
              )
          )
      ),
      size: "70%",
    }),

  // @ts-expect-error TS2554
  double_autocomplete: el => init(double_autocomplete, el),
  filelist: el =>
    import(/* webpackChunkName: "history" */ "../comp/files/Filelist").then(m =>
      // @ts-expect-error TS2345
      init(m.default, el, {
        filelist: el.getAttribute("data-filelist"),
      })
    ),
  imageload: el => {
    const strip = () => {
      el.removeAttribute("loading")
    }

    if (el.complete) {
      strip()
    } else {
      el.addEventListener("load", strip)
    }
  },
  // @ts-expect-error TS2554
  markdown: require_staff(el =>
    // @ts-expect-error TS2345
    init(markdown, el, { placeholder: el.getAttribute("placeholder") })
  ),

  megamenu: el => megamenu(el),
  // @ts-expect-error TS2554
  publicsearch: el => init(publicsearch, el),
  // @ts-expect-error TS2554
  rating: el => init(rating, el),
  // @ts-expect-error TS2554
  report: require_staff(el =>
    import(/* webpackChunkName: "report" */ "../comp/Report").then(m =>
      // @ts-expect-error TS2345
      init(m.default, el, {
        report: JSONDecode(first(selector("script", el)).innerHTML),
      })
    )
  ),
  // @ts-expect-error TS2554
  routerview: el => init(routerview, el),
  scroll: el => scroll(el),
  searchselect: el =>
    // @ts-expect-error TS2345
    init(searchselect, el, {
      multiple: el.matches("[multiple]"),
      options: object_map(
        (opt: HTMLOptionElement) => opt.value,
        (opt: HTMLOptionElement) => ({
          group: (opt.parentNode as HTMLElement)?.getAttribute("label") || null,
          id: opt.value,
          unicode: opt.innerHTML,
        }),
        selector("option", el) as HTMLOptionElement[]
      ),
      value: map(
        (opt: HTMLOptionElement) => opt.value,
        selector("option[selected]", el) as HTMLOptionElement[]
      ),
    }),

  sizedatalist: el => {
    const component = element("div")
    const id = uuid()

    el.setAttribute("list", id)

    append(el.parentNode, component)

    init(
      // @ts-expect-error TS2345
      sizedatalist,
      component,
      RecordMerge(
        object_map(
          k => k,
          k => JSONDecode(el.getAttribute(`data-${k}`) || "[]"),
          ["units", "min", "max"]
        ),
        { autocomplete: el.getAttribute("data-autocomplete"), id }
      )
    )
  },

  slideshow: el => {
    if (!el.getAttribute("href"))
      el.addEventListener("click", e => {
        e.preventDefault()
        e.stopPropagation()

        navigate(SLIDESHOW_VIEW, {
          selected: el.getAttribute("data-slide-src") || el.getAttribute("src"),
          selector: el.getAttribute("data-selector"),
        })
      })
  },

  spreadsheet: el =>
    import(/* webpackChunkName: "spreadsheet" */ "../comp/Spreadsheet").then(
      m =>
        // @ts-expect-error TS2345
        init(m.default, el, {
          errors: JSONDecode(el.getAttribute("data-errors")),
          input: JSONDecode(el.getAttribute("value") || "[{}]"),
          name: el.getAttribute("name"),
          schema: JSONDecode(el.getAttribute("data-schema")),
        })
    ),

  toggleclass: el => {
    const classname = el.getAttribute("data-class")
    const target = el.getAttribute("data-target")
    el.addEventListener("click", _e =>
      scan(
        e =>
          e.classList.contains(classname)
            ? e.classList.remove(classname)
            : e.classList.add(classname),
        selector(target)
      )
    )
  },
  uploader: async el =>
    // @ts-expect-error TS2345
    init(uploader, el, {
      filelist: el.getAttribute("data-filelist"),
    }),
  // @ts-expect-error TS2554
  username: el => init(username, el),
}

// @ts-expect-error TS2554
init(messages)

register_dom_update({
  after: () => {
    scan(el => {
      const func = components[el.getAttribute("data-component")]
      if (func) func(el)
      el.removeAttribute("data-component")
    }, selector("[data-component]"))

    scan(components.searchselect, selector("select[multiple]"))
    scan(components.imageload, selector("[loading=lazy]"))
  },
  login: () =>
    user().is_staff
      ? import(
          /* webpackChunkName: "admin" */ "../comp/files/Filesink"
          // @ts-expect-error TS2554
        ).then(m => init(m.default))
      : null,
})
